/** @file loadPuppetBundle: fetch an export bundle, check it with export, decode its sheets, bind clips. */
import { checkBundle } from '@zilo/pose3d-export/check/checkBundle';
import { readBundleFiles } from '@zilo/pose3d-export/files/readBundleFiles';
import { fromUtf8 } from '@zilo/pose3d-export/text/fromUtf8';
import type { ExportBundle } from '@zilo/pose3d-export/types/bundle';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import { bindAttachmentClips } from '@zilo/pose3d-pose/attach/bindAttachmentClips';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { toPose3DError } from '@zilo/pose3d-std/errors/toPose3DError';
import { decodeSheet } from '@zilo/pose3d-runtime-babylon/bundle/decodeSheet';
import { fetchBytes, type FetchBytes } from '@zilo/pose3d-runtime-babylon/bundle/fetchBytes';
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import { toRuntimePuppet } from '@zilo/pose3d-runtime-babylon/bundle/toRuntimePuppet';
import { RUNTIME_ERRORS } from '@zilo/pose3d-runtime-babylon/errors/runtimeErrors';

type Files = Record<string, Uint8Array | string>;

const MESH_FILE = /^meshes\/(.+)\.glb$/;

/** `fetchFn(url)`; a rejection that is not already a Pose3D error becomes `BUNDLE_FETCH` naming the URL. */
async function fetchOne(fetchFn: FetchBytes, url: string): Promise<Uint8Array> {
  try {
    return await fetchFn(url);
  } catch (e) {
    if (toPose3DError(e) !== null) throw e;
    return throwError(RUNTIME_ERRORS.BUNDLE_FETCH, { url, reason: e instanceof Error ? e.message : String(e) }, { path: url });
  }
}

/** The manifest and every file it lists (`.json` as text, the rest as bytes), keyed by bundle path. */
async function fetchFiles(baseUrl: string, manifestUrl: string, fetchFn: FetchBytes): Promise<Files> {
  const manifestText = fromUtf8(await fetchOne(fetchFn, manifestUrl));
  let listed: unknown;
  try {
    listed = (JSON.parse(manifestText) as { files?: unknown }).files;
  } catch (e) {
    return throwError(RUNTIME_ERRORS.BUNDLE_INVALID, { reason: e instanceof Error ? e.message : String(e) }, { path: '/files/manifest.json' });
  }
  const paths = typeof listed === 'object' && listed !== null ? Object.keys(listed) : [];
  const bytes = await Promise.all(paths.map((p) => fetchOne(fetchFn, `${baseUrl}${p}`)));
  const files: Files = { 'manifest.json': manifestText };
  paths.forEach((p, i) => {
    const b = bytes[i] ?? new Uint8Array(0);
    files[p] = p.endsWith('.json') ? fromUtf8(b) : b;
  });
  return files;
}

/** `readBundleFiles(files)`; its errors become `BUNDLE_INVALID` at the same path. */
function readFiles(files: Files): ExportBundle {
  try {
    return readBundleFiles(files);
  } catch (e) {
    const err = toPose3DError(e);
    return throwError(RUNTIME_ERRORS.BUNDLE_INVALID, { errors: [err ?? String(e)] }, { path: err?.path ?? '/', hint: err?.message ?? String(e) });
  }
}

/** `readFiles` then `checkBundle`; a failed check is `BUNDLE_INVALID` at the first error's path. */
function checkedBundle(files: Files): { bundle: ExportBundle; warnings: LoadedBundle['warnings'] } {
  const bundle = readFiles(files);
  const check = checkBundle(bundle, { files });
  const first = check.errors[0] ?? { path: '/', message: 'bundle check failed' };
  if (!check.ok) return throwError(RUNTIME_ERRORS.BUNDLE_INVALID, { errors: check.errors }, { path: first.path, hint: first.message });
  return { bundle, warnings: check.warnings };
}

/** The decoded sheets in `Object.keys(puppet.sheets)` order; absent PNG or a wrong decoded size throws. */
async function decodeSheets(bundle: ExportBundle): Promise<{ names: string[]; images: Image[] }> {
  const names = Object.keys(bundle.puppet.sheets);
  const images = await Promise.all(names.map(async (name) => {
    const path = `/puppet/sheets/${name}`;
    const png = bundle.sheets[name]?.png ?? throwError(RUNTIME_ERRORS.SHEET_MISSING, { sheet: name }, { path });
    const image = await decodeSheet(png);
    const size = bundle.puppet.sheets[name]?.size ?? [0, 0];
    if (image.width !== size[0] || image.height !== size[1]) {
      throwError(RUNTIME_ERRORS.SHEET_SIZE_MISMATCH, { sheet: name, expected: size, actual: [image.width, image.height] }, { path });
    }
    return image;
  }));
  return { names, images };
}

/**
 * Loads the bundle whose `manifest.json` is at `manifestUrl`: every listed file is fetched relative to it, read by
 * export's `readBundleFiles` and checked by `checkBundle(bundle, { files })` (errors -> `BUNDLE_INVALID`, warnings
 * kept), sheets decoded by image-io (`SHEET_MISSING`, `SHEET_SIZE_MISMATCH`), `meshes/<id>.glb` kept as bytes, and
 * the attachment clips bound to the runtime puppet (pose's `bindAttachmentClips`; a no-op without attachments).
 */
export async function loadPuppetBundle(manifestUrl: string, fetchFn: FetchBytes = fetchBytes): Promise<LoadedBundle> {
  const baseUrl = manifestUrl.slice(0, manifestUrl.lastIndexOf('/') + 1);
  const files = await fetchFiles(baseUrl, manifestUrl, fetchFn);
  const { bundle, warnings } = checkedBundle(files);
  const sheets = await decodeSheets(bundle);
  const meshes: Record<string, Uint8Array> = {};
  for (const [path, content] of Object.entries(files)) {
    const id = MESH_FILE.exec(path)?.[1];
    if (id !== undefined && content instanceof Uint8Array) meshes[id] = content;
  }
  bindAttachmentClips(toRuntimePuppet(bundle.puppet), Object.values(bundle.clips));
  return {
    baseUrl, bundle, puppet: bundle.puppet, profile: bundle.puppet.profileData, clips: bundle.clips,
    sheetNames: sheets.names, sheets: sheets.images, meshes, warnings,
  };
}
